import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  getAssignment,
  getAssignmentSubmissions,
  gradeSubmission,
  returnSubmission,
  requestResubmission,
  getAssignmentAnalytics
} from '../../api/educentialApi';
import './AssignmentGrading.css';

function AssignmentGrading() {
  const { assignmentId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [assignment, setAssignment] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [gradingData, setGradingData] = useState({ grade: '', feedback: '' });
  const [notification, setNotification] = useState(null);
  const [filter, setFilter] = useState('ALL'); // ALL, SUBMITTED, GRADED, LATE

  useEffect(() => {
    loadData();
  }, [assignmentId]);

  const loadData = async () => {
    try {
      setLoading(true);

      // Load assignment details
      const assignmentResponse = await getAssignment(assignmentId);
      if (assignmentResponse.success) {
        setAssignment(assignmentResponse.data);
      }

      // Load submissions
      const submissionsResponse = await getAssignmentSubmissions(assignmentId);
      if (submissionsResponse.success) {
        setSubmissions(submissionsResponse.data);
      }

      // Load analytics
      const analyticsResponse = await getAssignmentAnalytics(assignmentId);
      if (analyticsResponse.success) {
        setAnalytics(analyticsResponse.data);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      showNotification('Failed to load assignment data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  const handleSelectSubmission = (submission) => {
    setSelectedSubmission(submission);
    setGradingData({
      grade: submission.grade || '',
      feedback: submission.feedback || ''
    });
  };

  const handleGradeChange = (field, value) => {
    setGradingData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmitGrade = async () => {
    if (!selectedSubmission) return;

    try {
      if (!gradingData.grade || gradingData.grade === '') {
        showNotification('Please enter a grade', 'error');
        return;
      }

      const grade = parseFloat(gradingData.grade);
      if (isNaN(grade) || grade < 0 || grade > assignment.maxScore) {
        showNotification(`Grade must be between 0 and ${assignment.maxScore}`, 'error');
        return;
      }

      const response = await gradeSubmission(selectedSubmission.id, {
        grade,
        feedback: gradingData.feedback
      });

      if (response.success) {
        showNotification('Submission graded successfully', 'success');
        loadData(); // Reload to get updated submissions
        setSelectedSubmission(null);
        setGradingData({ grade: '', feedback: '' });
      } else {
        showNotification(response.error || 'Failed to grade submission', 'error');
      }
    } catch (error) {
      console.error('Error grading submission:', error);
      showNotification(error.response?.data?.error || 'Failed to grade submission', 'error');
    }
  };

  const handleReturnSubmission = async () => {
    if (!selectedSubmission) return;

    try {
      const response = await returnSubmission(selectedSubmission.id, gradingData.feedback);

      if (response.success) {
        showNotification('Submission returned to student', 'success');
        loadData();
        setSelectedSubmission(null);
        setGradingData({ grade: '', feedback: '' });
      } else {
        showNotification(response.error || 'Failed to return submission', 'error');
      }
    } catch (error) {
      console.error('Error returning submission:', error);
      showNotification(error.response?.data?.error || 'Failed to return submission', 'error');
    }
  };

  const handleRequestResubmission = async () => {
    if (!selectedSubmission) return;

    try {
      if (!gradingData.feedback.trim()) {
        showNotification('Please provide feedback for resubmission request', 'error');
        return;
      }

      const response = await requestResubmission(selectedSubmission.id, gradingData.feedback);

      if (response.success) {
        showNotification('Resubmission requested', 'success');
        loadData();
        setSelectedSubmission(null);
        setGradingData({ grade: '', feedback: '' });
      } else {
        showNotification(response.error || 'Failed to request resubmission', 'error');
      }
    } catch (error) {
      console.error('Error requesting resubmission:', error);
      showNotification(error.response?.data?.error || 'Failed to request resubmission', 'error');
    }
  };

  const getFilteredSubmissions = () => {
    switch (filter) {
      case 'SUBMITTED':
        return submissions.filter(s => s.status === 'SUBMITTED');
      case 'GRADED':
        return submissions.filter(s => s.status === 'GRADED');
      case 'LATE':
        return submissions.filter(s => s.isLate);
      default:
        return submissions;
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const badges = {
      SUBMITTED: { label: 'Submitted', className: 'status-submitted' },
      GRADED: { label: 'Graded', className: 'status-graded' },
      RETURNED: { label: 'Returned', className: 'status-returned' },
      RESUBMIT_REQUESTED: { label: 'Resubmit Requested', className: 'status-resubmit' }
    };
    const badge = badges[status] || { label: status, className: '' };
    return <span className={`status-badge ${badge.className}`}>{badge.label}</span>;
  };

  if (loading) {
    return (
      <div className="assignment-grading">
        <div className="loading-spinner">Loading submissions...</div>
      </div>
    );
  }

  if (!assignment) {
    return (
      <div className="assignment-grading">
        <div className="error-message">Assignment not found</div>
      </div>
    );
  }

  const filteredSubmissions = getFilteredSubmissions();

  return (
    <div className="assignment-grading">
      {notification && (
        <div className={`notification notification-${notification.type}`}>
          {notification.message}
        </div>
      )}

      {/* Header */}
      <div className="grading-header">
        <div>
          <h1>{assignment.title}</h1>
          <p className="assignment-meta">
            {assignment.Lesson?.Module?.Course?.title} • Max Score: {assignment.maxScore} points
          </p>
        </div>
        <button onClick={() => navigate(-1)} className="btn-secondary">
          Back to Course
        </button>
      </div>

      {/* Analytics Summary */}
      {analytics && (
        <div className="analytics-summary">
          <div className="stat-card">
            <div className="stat-value">{analytics.totalSubmissions}</div>
            <div className="stat-label">Total Submissions</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{analytics.gradedSubmissions}</div>
            <div className="stat-label">Graded</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{analytics.lateSubmissions}</div>
            <div className="stat-label">Late</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{analytics.avgGrade}%</div>
            <div className="stat-label">Average Score</div>
          </div>
        </div>
      )}

      <div className="grading-content">
        {/* Submissions List */}
        <div className="submissions-panel">
          <div className="panel-header">
            <h2>Submissions ({filteredSubmissions.length})</h2>
            <div className="filter-buttons">
              <button
                className={filter === 'ALL' ? 'active' : ''}
                onClick={() => setFilter('ALL')}
              >
                All
              </button>
              <button
                className={filter === 'SUBMITTED' ? 'active' : ''}
                onClick={() => setFilter('SUBMITTED')}
              >
                Pending
              </button>
              <button
                className={filter === 'GRADED' ? 'active' : ''}
                onClick={() => setFilter('GRADED')}
              >
                Graded
              </button>
              <button
                className={filter === 'LATE' ? 'active' : ''}
                onClick={() => setFilter('LATE')}
              >
                Late
              </button>
            </div>
          </div>

          <div className="submissions-list">
            {filteredSubmissions.length === 0 ? (
              <div className="empty-state">No submissions found</div>
            ) : (
              filteredSubmissions.map((submission) => (
                <div
                  key={submission.id}
                  className={`submission-item ${
                    selectedSubmission?.id === submission.id ? 'selected' : ''
                  }`}
                  onClick={() => handleSelectSubmission(submission)}
                >
                  <div className="submission-student">
                    <div className="student-name">{submission.User.fullName}</div>
                    <div className="student-email">{submission.User.email}</div>
                  </div>
                  <div className="submission-info">
                    {getStatusBadge(submission.status)}
                    {submission.isLate && <span className="late-badge">Late</span>}
                    {submission.grade !== null && (
                      <span className="grade-display">
                        {submission.grade}/{assignment.maxScore}
                      </span>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Grading Panel */}
        <div className="grading-panel">
          {selectedSubmission ? (
            <>
              <div className="panel-header">
                <h2>Grade Submission</h2>
                <button
                  className="btn-link"
                  onClick={() => setSelectedSubmission(null)}
                >
                  Clear
                </button>
              </div>

              <div className="submission-details">
                <div className="detail-group">
                  <label>Student</label>
                  <div className="detail-value">{selectedSubmission.User.fullName}</div>
                </div>

                <div className="detail-group">
                  <label>Submitted At</label>
                  <div className="detail-value">
                    {formatDate(selectedSubmission.submittedAt)}
                    {selectedSubmission.isLate && (
                      <span className="late-indicator">
                        {' '}({selectedSubmission.daysLate} day{selectedSubmission.daysLate !== 1 ? 's' : ''} late)
                      </span>
                    )}
                  </div>
                </div>

                <div className="detail-group">
                  <label>Status</label>
                  <div className="detail-value">
                    {getStatusBadge(selectedSubmission.status)}
                  </div>
                </div>

                {selectedSubmission.textResponse && (
                  <div className="detail-group">
                    <label>Text Response</label>
                    <div className="text-response">{selectedSubmission.textResponse}</div>
                  </div>
                )}

                {selectedSubmission.fileUrl && (
                  <div className="detail-group">
                    <label>Submitted File</label>
                    <div className="file-info">
                      <span className="file-name">{selectedSubmission.fileName}</span>
                      <a
                        href={`${process.env.REACT_APP_API_BASE_URL || 'https://api.educential.com'}/${selectedSubmission.fileUrl}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn-link"
                      >
                        Download
                      </a>
                    </div>
                  </div>
                )}

                <div className="grading-form">
                  <div className="form-group">
                    <label>Grade (out of {assignment.maxScore})</label>
                    <input
                      type="number"
                      value={gradingData.grade}
                      onChange={(e) => handleGradeChange('grade', e.target.value)}
                      min="0"
                      max={assignment.maxScore}
                      className="form-control"
                      placeholder="Enter grade"
                    />
                  </div>

                  <div className="form-group">
                    <label>Feedback</label>
                    <textarea
                      value={gradingData.feedback}
                      onChange={(e) => handleGradeChange('feedback', e.target.value)}
                      className="form-control"
                      rows="6"
                      placeholder="Provide feedback to the student"
                    />
                  </div>

                  <div className="grading-actions">
                    <button onClick={handleSubmitGrade} className="btn-primary">
                      Submit Grade
                    </button>
                    <button onClick={handleReturnSubmission} className="btn-secondary">
                      Return (No Grade)
                    </button>
                    <button onClick={handleRequestResubmission} className="btn-warning">
                      Request Resubmission
                    </button>
                  </div>
                </div>

                {selectedSubmission.status === 'GRADED' && selectedSubmission.GradedBy && (
                  <div className="graded-info">
                    <small>
                      Graded by {selectedSubmission.GradedBy.fullName} on{' '}
                      {formatDate(selectedSubmission.gradedAt)}
                    </small>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="empty-selection">
              <p>Select a submission to grade</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default AssignmentGrading;
